import { useEffect, useRef, useState } from "react";

/**
 * Hook that triggers a CSS class when the element enters the viewport.
 * @param {Object} options
 * @param {number} options.threshold - 0-1, how much of the element must be visible
 * @param {string} options.rootMargin - margin around root
 * @param {boolean} options.once - if true, only triggers once
 */
export const useScrollReveal = ({ threshold = 0.15, rootMargin = "0px 0px -60px 0px", once = true } = {}) => {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          if (once) observer.unobserve(el);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isRevealed };
};

/**
 * Wrapper component that animates children on scroll.
 * @param {string} variant - 'up' | 'left' | 'right' | 'scale' | 'stagger'
 * @param {number} delay - additional delay in ms
 */
export const ScrollReveal = ({ children, variant = "up", delay = 0, className = "", ...props }) => {
  const { ref, isRevealed } = useScrollReveal();

  const variantClass = {
    up: "scroll-reveal",
    left: "scroll-reveal-left",
    right: "scroll-reveal-right",
    scale: "scroll-reveal-scale",
    stagger: "scroll-reveal-stagger",
  }[variant] || "scroll-reveal";

  return (
    <div
      ref={ref}
      className={`${variantClass} ${isRevealed ? "revealed" : ""} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...props}
    >
      {children}
    </div>
  );
};
